import type { FieldEditorValues, SectionEditorValues } from './config';

export type UniqueIdentifier = string | number;

export interface FieldSummary {
  name: string;
  type: string;
  required: boolean;
  readOnly?: boolean;
  hidden?: boolean;
  isCustom?: boolean;
  forcedRequired?: boolean;
  canEditLabel?: boolean;
  canEditOptions?: boolean;
  canDelete?: boolean;
  subtitle?: string;
  isGroup?: boolean;
  originalLabel?: string;
}

export const DEFAULT_LANG = {
  formEditor: {
    title: 'Form editor',
    description: 'Drag and drop to reorder sections and fields',
    save: 'Save',
    cancel: 'Cancel',
    addField: 'Add field',
    addSection: 'Add section',
  },
  sectionBuilder: {
    title: 'Section editor',
    addSave: 'Add section',
    editSave: 'Edit section',
    cancel: 'Cancel',
  },
  fieldEditor: {
    title: 'Field editor',
    addSave: 'Add field',
    editSave: 'Save',
    cancel: 'Cancel',
  },
};

export const getFieldTypeLabel = (
  type: string,
  options?: { value: string; label: string }[]
): string => {
  if (options) {
    const match = options.find((o) => o.value === type);
    if (match) {
      return match.label;
    }
  }

  // Titlecase the raw type string as a fallback for standard field display
  // types (e.g. 'dropdown', 'date', 'number') that aren't in the custom
  // attribute field type options.
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
};

export const buildSectionDefaults = (
  sectionId: string | null,
  sectionNames: Record<string, string>,
  sectionConfigs: Record<string, SectionEditorValues>
): SectionEditorValues => {
  if (!sectionId) {
    return {
      name: '',
      description: '',
      conditionalLogicEnabled: false,
      conditionalLogicOperation: 'and' as const,
      conditionalLogicRules: [],
      guidanceEnabled: false,
      guidance: '',
    };
  }
  const config = sectionConfigs[sectionId];
  if (config) {
    return config;
  }

  return {
    name: sectionNames[sectionId] ?? '',
    description: '',
    conditionalLogicEnabled: false,
    conditionalLogicOperation: 'and' as const,
    conditionalLogicRules: [],
    guidanceEnabled: false,
    guidance: '',
  };
};

export const buildFieldDefaults = (
  fieldId: string | null,
  fields: Record<string, FieldSummary>,
  fieldConfigs: Record<string, FieldEditorValues>
): FieldEditorValues => {
  if (!fieldId) {
    return {
      fieldType: 'text',
      fieldName: '',
      required: false,
      readOnly: false,
      hidden: false,
      defaultValueEnabled: false,
      defaultValue: '',
      options: [],
      conditionalLogicEnabled: false,
      conditionalLogicOperation: 'and' as const,
      conditionalLogicRules: [],
      guidanceEnabled: false,
      guidance: '',
    };
  }
  const config = fieldConfigs[fieldId];
  if (config) {
    return config;
  }

  const field = fields[fieldId];

  return {
    fieldType: field?.type ?? '',
    fieldName: field?.name ?? '',
    required: field?.required ?? false,
    readOnly: field?.readOnly ?? false,
    hidden: field?.hidden ?? false,
    defaultValueEnabled: false,
    defaultValue: '',
    options: [],
    conditionalLogicEnabled: false,
    conditionalLogicOperation: 'and' as const,
    conditionalLogicRules: [],
    guidanceEnabled: false,
    guidance: '',
  };
};
